/**
 * Manager Scenario Unlock Service
 *
 * Application service that orchestrates the certification unlock workflow for managed users.
 * This service coordinates two concerns:
 * 1. Business rule validation (via the certification unlock policy)
 * 2. API orchestration (updating the backend state)
 *
 * The service follows a fail-fast pattern: policy decisions are evaluated first,
 * preventing unnecessary API calls when unlock is not permitted.
 */

import { unlockUserScenarioById } from '@/lib/api/simulations';
import {
  evaluateCertificationUnlock,
  CertificationUnlockInput,
  CertificationUnlockReason,
} from '@/domains/management';

// ================================ Result Types ================================

/**
 * Successful unlock result.
 * Indicates the unlock request passed all policy checks and the API call succeeded.
 */
type ResultTrue = {
  success: true;
  reason: 'ok';
};

/**
 * Failed unlock result.
 * Indicates either:
 * - A policy rule prevented the unlock (e.g., not a direct report, already certified)
 * - The API call failed (non-200 response code)
 *
 * The reason field provides callers with granular error context for user-facing messaging.
 */
type ResultFalse = {
  success: false;
  reason: Exclude<CertificationUnlockReason, 'ok'> | 'api_failed';
};

/**
 * Discriminated union type representing all possible outcomes of an unlock request.
 * Use the `success` discriminator to narrow the type in calling code.
 */
export type ManagerCertificationUnlockResult = ResultTrue | ResultFalse;


// ================================ Service Implementation ================================

/**
 * Orchestrates the unlock flow for a certification scenario assigned to a managed user.
 *
 * Workflow:
 * 1. Evaluate the unlock request against the certification policy (domain rules)
 * 2. If policy denies the request, return the denial reason immediately (no API call)
 * 3. If policy allows, proceed with the API call to persist the unlock state
 * 4. Map API response codes to caller-facing result
 *
 * @param input - Unlock context including manager, user, certification, and lock state
 * @returns A discriminated union indicating success or the reason for failure
 *
 * @example
 * const result = await unlockCertificationForManagedUser({
 *   managerUserId: 'mgr-123',
 *   targetUserId: 'user-456',
 *   scenarioId: 100,
 *   activeCertificationId: 100,
 *   directReportUserIds: ['user-456', 'user-789'],
 *   targetUserMostRecentCertPassed: false,
 *   targetUserScenarioLocked: true,
 * });
 *
 * if (result.success) {
 *   // User's certification lock has been removed; show success message
 * } else {
 *   // Display the denial reason to the manager via MANAGER_LABELS.UNLOCK_DENIAL_*
 * }
 */
export const unlockCertificationForManagedUser = async (
  input: CertificationUnlockInput,
): Promise<ManagerCertificationUnlockResult> => {
  // ──────────────────────────────────────────────────────────────────────────────
  // Step 1: Evaluate business rules
  // ──────────────────────────────────────────────────────────────────────────────
  // The certification unlock policy enforces six strict validation rules:
  // - All required context (manager, user, scenario, certification IDs) is present
  // - The target user is a direct report of the manager
  // - The scenario ID matches the active certification ID (unlock scope validation)
  // - The user has not already passed the most recent certification attempt
  // - The certification is currently in a locked state
  //
  // This evaluation happens entirely in memory—no IO or side effects.
  const decision = evaluateCertificationUnlock(input);

  // If any policy rule is violated, return the denial reason immediately.
  // This prevents unnecessary API calls and provides clear feedback to the caller.
  if (!decision.allowed) {
    // Defensive fallback: treat 'ok' reason as 'missing_context' (should never happen)
    const denialReason = decision.reason === 'ok' ? 'missing_context' : decision.reason;

    return {
      success: false,
      reason: denialReason,
    };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Step 2: Validate required context for the API call
  // ──────────────────────────────────────────────────────────────────────────────
  // Although the policy has already validated this, TypeScript requires a runtime
  // check to narrow the type. This prevents potential null-pointer exceptions
  // when calling the API.
  if (input.scenarioId == null) {
    return {
      success: false,
      reason: 'missing_context',
    };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Step 3: Persist the unlock to the backend
  // ──────────────────────────────────────────────────────────────────────────────
  // Call the API to remove the lock from the user's certification scenario.
  // The API client returns the HTTP response code (200, 404, 500, etc.)
  // without throwing on non-2xx status; this allows us to handle all outcomes uniformly.
  const responseCode = await unlockUserScenarioById(input.scenarioId, input.targetUserId);

  // ──────────────────────────────────────────────────────────────────────────────
  // Step 4: Interpret the API response
  // ──────────────────────────────────────────────────────────────────────────────
  // Any response code other than 200 indicates failure (missing resource, server error, etc.)
  if (responseCode !== 200) {
    return {
      success: false,
      reason: 'api_failed',
    };
  }

  // Success: the policy allowed the unlock and the backend persisted the change.
  return {
    success: true,
    reason: 'ok',
  };
};
